import { useState, type ReactElement } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { apiClient } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { AdminNav } from "./AdminPage";

/**
 * Cycle 17 STORY-1703 — Admin Leads management UI.
 *
 * Backend (Cycle 11) уже live:
 * - GET    /api/admin/leads (list, paginated, filterable)
 * - PATCH  /api/admin/leads/{id} (status update)
 * - GET    /api/admin/leads/export.csv
 *
 * Эта страница — UI для sales team to manage leads без psql / curl.
 */

const STATUSES = ["new", "contacted", "qualified", "closed_won", "closed_lost"] as const;
type LeadStatus = (typeof STATUSES)[number];

const STATUS_LABELS: Record<LeadStatus, { ru: string; color: string; bg: string }> = {
  new: { ru: "Новый", color: "#0b5fff", bg: "#e6f0ff" },
  contacted: { ru: "Контакт", color: "#a07000", bg: "#fff7d6" },
  qualified: { ru: "Квалифицирован", color: "#0a7a4a", bg: "#e6f7ee" },
  closed_won: { ru: "Закрыт ✓", color: "#0a7a4a", bg: "#d6f5e3" },
  closed_lost: { ru: "Закрыт ✗", color: "#a01c2a", bg: "#fde8eb" },
};

type Lead = {
  id: string;
  name: string;
  email: string;
  company: string;
  role: string | null;
  phone: string | null;
  products_of_interest: string[];
  message: string | null;
  source_ip: string | null;
  status: string;
  created_at: string;
};

type LeadList = {
  items: Lead[];
  total: number;
  page: number;
  page_size: number;
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("ru", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

export function AdminLeadsPage(): ReactElement {
  const { hasAdminJwt } = useAuth();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "">("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [page, setPage] = useState(1);
  // B2: результат конвертации лида в invite (ссылка для отправки клиенту)
  const [inviteResult, setInviteResult] = useState<{ url: string; client_name: string; subset: number } | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, error } = useQuery<LeadList>({
    queryKey: ["admin-leads", statusFilter, companyFilter, page],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, page_size: 50 };
      if (statusFilter) params.status = statusFilter;
      if (companyFilter) params.company = companyFilter;
      const res = await apiClient.get<LeadList>("/api/admin/leads", { params });
      return res.data;
    },
    enabled: hasAdminJwt,
    refetchInterval: 60_000,
  });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: LeadStatus }) => {
      const res = await apiClient.patch(`/api/admin/leads/${id}`, { status });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-leads"] });
    },
  });

  // B2: лид → персональный invite каталога (ссылка для клиента)
  const createInvite = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<{ url: string; client_name: string; catalog_subset: string[] | null }>(
        `/api/admin/leads/${id}/invite`,
      );
      return res.data;
    },
    onSuccess: (data) => {
      setCopied(false);
      setInviteResult({
        url: data.url,
        client_name: data.client_name,
        subset: data.catalog_subset ? data.catalog_subset.length : 0,
      });
    },
  });

  async function copyInviteUrl(): Promise<void> {
    if (!inviteResult) return;
    try {
      await navigator.clipboard.writeText(inviteResult.url);
      setCopied(true);
    } catch {
      /* clipboard может быть недоступен — пользователь скопирует вручную */
    }
  }

  function exportCsv(): void {
    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    const url = `/api/admin/leads/export.csv${params.toString() ? "?" + params.toString() : ""}`;
    window.open(url, "_blank");
  }

  if (!hasAdminJwt) {
    return (
      <section style={{ padding: 32, maxWidth: 600, margin: "0 auto" }}>
        <h1>Admin Leads</h1>
        <p style={{ color: "#5a6378" }}>
          Требуется admin authentication. <Link to="/admin">Войти как admin</Link>
        </p>
      </section>
    );
  }

  return (
    <section style={{ maxWidth: 1300, margin: "0 auto" }}>
      <AdminNav />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap", gap: 12, padding: "0 24px" }}>
        <div>
          <h1 style={{ margin: "0 0 4px" }}>Leads ({data?.total ?? "..."})</h1>
          <p style={{ margin: 0, fontSize: 13, color: "#5a6378" }}>Sales pipeline · Cycle 11+17</p>
        </div>
        <button
          onClick={exportCsv}
          style={{
            padding: "8px 14px",
            background: "#0b5fff",
            color: "white",
            border: 0,
            borderRadius: 6,
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          📥 Export CSV
        </button>
      </header>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value as LeadStatus | "");
            setPage(1);
          }}
          style={{ padding: "8px 12px", border: "1px solid #d1d5e0", borderRadius: 6, fontSize: 13 }}
        >
          <option value="">Все статусы</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s].ru}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Поиск по компании..."
          value={companyFilter}
          onChange={(e) => {
            setCompanyFilter(e.target.value);
            setPage(1);
          }}
          style={{ padding: "8px 12px", border: "1px solid #d1d5e0", borderRadius: 6, fontSize: 13, flex: 1, minWidth: 200 }}
        />
      </div>

      {inviteResult && (
        <div style={{ margin: "0 0 16px", padding: 16, background: "#e6f7ee", border: "1px solid #0a7a4a", borderRadius: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <strong style={{ color: "#0a7a4a" }}>
              ✅ Ссылка для «{inviteResult.client_name}» готова{" "}
              <span style={{ fontWeight: 400, color: "#5a6378" }}>
                ({inviteResult.subset > 0 ? `${inviteResult.subset} продукт(ов)` : "весь каталог"})
              </span>
            </strong>
            <button onClick={() => setInviteResult(null)} style={{ background: "none", border: 0, cursor: "pointer", fontSize: 18, color: "#5a6378" }}>×</button>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <input
              readOnly
              value={inviteResult.url}
              onFocus={(e) => e.currentTarget.select()}
              style={{ flex: 1, minWidth: 280, padding: "8px 12px", border: "1px solid #b8d8c6", borderRadius: 6, fontSize: 13, fontFamily: "monospace" }}
            />
            <button
              onClick={copyInviteUrl}
              style={{ padding: "8px 14px", background: copied ? "#0a7a4a" : "#0b5fff", color: "white", border: 0, borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}
            >
              {copied ? "✓ Скопировано" : "Копировать"}
            </button>
          </div>
          <p style={{ margin: "8px 0 0", fontSize: 12, color: "#5a6378" }}>
            Отправьте ссылку клиенту — он войдёт в портал и увидит подобранный набор продуктов.
          </p>
        </div>
      )}

      {isLoading && <p>Загрузка...</p>}
      {error && <p style={{ color: "#a01c2a" }}>Ошибка загрузки</p>}

      {data && data.items.length === 0 && (
        <div style={{ padding: 48, textAlign: "center", color: "#8693b0", border: "1px dashed #d1d5e0", borderRadius: 12 }}>
          Лидов пока нет. Попробуйте сменить фильтры или дождитесь первого запроса.
        </div>
      )}

      {data && data.items.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", background: "white", border: "1px solid #e1e6f0", borderRadius: 8, overflow: "hidden" }}>
          <thead>
            <tr style={{ background: "#f5f7fc", textAlign: "left", fontSize: 12, color: "#5a6378" }}>
              <th style={{ padding: 10 }}>Дата</th>
              <th style={{ padding: 10 }}>Контакт</th>
              <th style={{ padding: 10 }}>Компания</th>
              <th style={{ padding: 10 }}>Email / Phone</th>
              <th style={{ padding: 10 }}>Продукты</th>
              <th style={{ padding: 10 }}>Статус</th>
              <th style={{ padding: 10 }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((lead) => (
              <tr key={lead.id} style={{ borderTop: "1px solid #e1e6f0", fontSize: 13 }}>
                <td style={{ padding: 10, color: "#5a6378", whiteSpace: "nowrap" }}>{formatDate(lead.created_at)}</td>
                <td style={{ padding: 10 }}>
                  <Link to={`/admin/leads/${lead.id}`} style={{ fontWeight: 600, color: "#0b5fff" }}>{lead.name}</Link>
                  {lead.role && <div style={{ fontSize: 11, color: "#8693b0" }}>{lead.role}</div>}
                </td>
                <td style={{ padding: 10 }}>{lead.company}</td>
                <td style={{ padding: 10, fontSize: 12 }}>
                  <a href={`mailto:${lead.email}`} style={{ color: "#0b5fff" }}>{lead.email}</a>
                  {lead.phone && <div style={{ marginTop: 2, color: "#5a6378" }}>📞 {lead.phone}</div>}
                </td>
                <td style={{ padding: 10, fontSize: 12 }}>
                  {(lead.products_of_interest || []).slice(0, 3).join(", ") || "—"}
                  {lead.products_of_interest.length > 3 && (
                    <span style={{ color: "#8693b0" }}> +{lead.products_of_interest.length - 3}</span>
                  )}
                </td>
                <td style={{ padding: 10 }}>
                  <select
                    value={lead.status}
                    onChange={(e) => updateStatus.mutate({ id: lead.id, status: e.target.value as LeadStatus })}
                    style={{
                      padding: "4px 8px",
                      border: "1px solid",
                      borderColor: STATUS_LABELS[lead.status as LeadStatus]?.color ?? "#d1d5e0",
                      borderRadius: 4,
                      fontSize: 12,
                      background: STATUS_LABELS[lead.status as LeadStatus]?.bg ?? "white",
                      color: STATUS_LABELS[lead.status as LeadStatus]?.color ?? "#0a1340",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s].ru}
                      </option>
                    ))}
                  </select>
                </td>
                <td style={{ padding: 10 }}>
                  <button
                    onClick={() => createInvite.mutate(lead.id)}
                    disabled={createInvite.isPending}
                    title="Создать персональную ссылку каталога для этого лида"
                    style={{ padding: "5px 10px", background: "#fff", color: "#0b5fff", border: "1px solid #0b5fff", borderRadius: 5, cursor: "pointer", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    🔗 Ссылка
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {data && data.total > data.page_size && (
        <div style={{ display: "flex", gap: 8, marginTop: 16, justifyContent: "center" }}>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
            ← Назад
          </button>
          <span style={{ padding: "0 12px", lineHeight: "32px" }}>
            Страница {page} из {Math.ceil(data.total / data.page_size)}
          </span>
          <button
            onClick={() => setPage((p) => p + 1)}
            disabled={page * data.page_size >= data.total}
          >
            Вперёд →
          </button>
        </div>
      )}
    </section>
  );
}
